import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
  DocLink,
} from "@/components/docs";
import { docsColors } from "@/lib/docs-theme";

export default function SlackIntegrationPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods", href: "/docs/notifications" },
    { label: "Slack" },
  ];

  return (
    <DocLayout
      title="Slack Integration - Uptime Monitor Documentation"
      description="Get instant Slack notifications for monitor alerts. Real-time updates in your team channels with rich formatting and interactive features."
      canonical="https://uptime-monitor.io/docs/notifications/slack"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Slack Integration"
        description="Send monitor alerts to Slack channels using incoming webhooks. Get notified when monitors go down or recover."
      />

      {/* Overview */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>How It Works</h2>
          <p className={`${docsColors.text.body} mb-4`}>
            Uptime Monitor sends alerts to Slack using incoming webhooks. When a monitor goes down
            or recovers, a formatted message is posted to your configured channel.
          </p>
          <ul className={`space-y-2 ${docsColors.text.body}`}>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Monitor name appears as a clickable link to the dashboard
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Down alerts show the error reason
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Recovery alerts include total downtime
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Webhook Setup */}
      <DocSection title="Setup Instructions">
        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 1: Create Webhook in Slack
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Go to{" "}
                      <DocLink href="https://api.slack.com/apps" external>
                        api.slack.com/apps
                      </DocLink>{" "}
                      and create a new app
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Select <strong className={docsColors.text.heading}>From scratch</strong> and
                      choose your workspace
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Navigate to{" "}
                      <strong className={docsColors.text.heading}>
                        Features → Incoming Webhooks
                      </strong>{" "}
                      and activate it
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click{" "}
                      <strong className={docsColors.text.heading}>
                        Add New Webhook to Workspace
                      </strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Select the channel for alerts and authorize
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={6} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Copy the <strong className={docsColors.text.heading}>Webhook URL</strong>
                    </p>
                  </div>
                </div>
              </li>
            </ol>
          </DocCard>
        </div>

        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 2: Configure in Uptime Monitor
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      In Uptime Monitor, go to{" "}
                      <strong className={docsColors.text.heading}>Settings → Contacts</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click <strong className={docsColors.text.heading}>Add Contact</strong> and
                      select <strong className={docsColors.text.heading}>Slack</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Enter a name (e.g., &quot;Production Alerts&quot;) and paste your webhook URL
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>Save the contact</p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Add the Slack contact to your monitors
                    </p>
                  </div>
                </div>
              </li>
            </ol>
          </DocCard>
        </div>
      </DocSection>

      {/* Message Format */}
      <DocSection title="Slack Message Format">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            Messages are formatted with the monitor name as a hyperlink:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Down Alert</h3>
              <DocCodeBlock className="border border-slate-700">
                <p className={`${docsColors.text.body}`}>
                  👻 <DocLink href="#">Production API</DocLink> is DOWN: Connection timeout after
                  10000ms.
                </p>
              </DocCodeBlock>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Recovery Alert</h3>
              <DocCodeBlock className="border border-slate-700">
                <p className={`${docsColors.text.body}`}>
                  👻 <DocLink href="#">Production API</DocLink> is UP! It was down for 5 minutes.
                </p>
              </DocCodeBlock>
            </div>
          </div>

          <DocAlert variant="info" className="mt-4 p-3">
            <p className={`text-sm ${docsColors.text.body}`}>
              <strong className={docsColors.text.heading}>Note:</strong> The monitor name links
              directly to the monitor details page in your dashboard.
            </p>
          </DocAlert>
        </DocCard>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use dedicated alert channels</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Create separate channels for different environments or severity levels
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Multiple contacts for redundancy
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Create multiple Slack contacts for different channels and assign to monitors
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Test webhook connectivity</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Verify webhooks are working by pausing and resuming a monitor
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Configure channel notifications</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Set up Slack channel notification preferences to avoid missing alerts
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="Troubleshooting">
        <div className="space-y-4">
          <DocAlert variant="warning" className="p-6">
            <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
              Common Issues
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                  Messages not appearing
                </h4>
                <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
                  <li>• Verify webhook URL is correct and complete</li>
                  <li>• Check channel exists and app has permission</li>
                  <li>• Ensure Slack contact is assigned to monitors</li>
                  <li>• Verify webhook is not disabled in Slack</li>
                </ul>
              </div>

              <div>
                <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>Webhook errors</h4>
                <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
                  <li>• Regenerate webhook URL in Slack</li>
                  <li>• Check for workspace restrictions</li>
                  <li>• Ensure webhook app is not deleted</li>
                  <li>• Verify network connectivity</li>
                </ul>
              </div>
            </div>
          </DocAlert>
        </div>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/notifications/webhooks", text: "Webhooks" }}
        next={{ href: "/docs/notifications/discord", text: "Discord" }}
      />
    </DocLayout>
  );
}
